import { logger } from "~/lib/logger";
import {
    CONTAINER_SNIFF_BYTES,
    startsWithEbmlHeader,
    startsWithFtypBox,
} from "~/lib/media-container";

// AIDEV-NOTE: Raw IndexedDB for per-chunk recording persistence.
// localforage can't do compound keys or indexes needed for chunk-by-session lookups.
// Each audio chunk is stored individually so partial recordings survive crashes.

const DB_NAME = "ifs-companion-recordings";
const DB_VERSION = 1;
const SESSIONS_STORE = "sessions";
const CHUNKS_STORE = "chunks";
const CHUNKS_BY_SESSION_INDEX = "bySessionId";
// AIDEV-NOTE: Fallback chunk duration used only when rebuilding meta for
// orphaned chunks whose sessions-store row is gone. The recorder's real value
// is 1000ms; this only affects the estimated duration shown in recovery UI.
const DEFAULT_ORPHAN_CHUNK_MS = 1000;

function nowIso(): string {
    return new Date().toISOString();
}

export type PersistedSessionStatus =
    | "recording"
    | "paused"
    | "stopped"
    | "failed"
    | "cancelled";

export interface PersistedSessionMeta {
    id: string;
    status: PersistedSessionStatus;
    mimeType: string;
    chunkMs: number;
    chunkCount: number;
    totalBytes: number;
    elapsedMs: number;
    partial: boolean;
    createdAt: string;
    updatedAt: string;
    startedAt: string;
    stoppedAt?: string;
    uploadSessionId?: string | null;
    // AIDEV-NOTE: Stored so crash recovery can bootstrap a fresh /begin-file-upload call
    practitionerId?: string | null;
    clientId?: string | null;
    // AIDEV-NOTE: IndexedDB is per-origin — recovery UIs filter on this so a
    // different account on the same browser never sees this recording.
    recordedByUserId?: string | null;
    webmDiagnostics?: PersistedRecordingWebmDiagnostics;
    // AIDEV-NOTE: Set by repairOrphanedChunkSessions when this row was rebuilt
    // from chunks left behind by the pre-fix index-based read/delete.
    recoveredFromOrphanedChunks?: boolean;
}

export interface PersistedChunkRecord {
    sessionId: string;
    index: number;
    createdAt: string;
    size: number;
    blob: Blob;
    diagnostics?: PersistedChunkDiagnostics;
}

export interface MaterializedRecording {
    session: PersistedSessionMeta;
    chunks: PersistedChunkRecord[];
    blob: Blob;
    integrity?: MaterializedRecordingIntegrity;
    webmDiagnostics?: PersistedRecordingWebmDiagnostics;
}

const dbPromiseCache = new Map<string, Promise<IDBDatabase>>();

// AIDEV-NOTE: Headerless WebM chunks are repairable; keep evidence, don't block upload.
// AIDEV-NOTE: 2026-10-21 — validation now covers MP4 too. The old `isWebmMimeType`
// gate returned {valid:true, reason:"not-webm"} for audio/mp4, so every Safari/iOS
// recording skipped inspection entirely; two of the five production files lost to
// a missing init segment were fragmented MP4 beginning at a `moof` box.
export type MaterializedRecordingIntegrityReason =
    | "not-webm"
    | "unsupported-container"
    | "valid"
    | "empty-webm"
    | "missing-first-chunk"
    | "missing-ebml-header"
    | "missing-mp4-init-segment"
    | "chunk-index-gap"
    | "header-read-failed";

export interface MaterializedRecordingIntegrity {
    valid: boolean;
    reason: MaterializedRecordingIntegrityReason;
    detail?: string;
}

export interface PersistedChunkDiagnostics {
    version: 1;
    index: number;
    size: number;
    type: string;
    firstBytesHex: string;
    startsWithEbmlHeader: boolean;
    hasOpusHeadNearStart: boolean;
}

export interface PersistedRecordingWebmDiagnostics {
    version: 1;
    mimeType: string;
    chunkMs: number;
    chunkCount: number;
    totalBytes: number;
    materializedBytes: number;
    integrity: MaterializedRecordingIntegrity;
    firstObservedChunkIndex: number | null;
    lastObservedChunkIndex: number | null;
    firstChunk: PersistedChunkDiagnostics | null;
    sampledChunks: PersistedChunkDiagnostics[];
}

const EBML_HEADER_BYTES = [0x1a, 0x45, 0xdf, 0xa3];
const OPUS_HEAD_BYTES = [0x4f, 0x70, 0x75, 0x73, 0x48, 0x65, 0x61, 0x64];
const CHUNK_DIAGNOSTIC_FIRST_BYTES = 16;
const CHUNK_DIAGNOSTIC_SCAN_BYTES = 256;
const RECORDING_DIAGNOSTIC_SAMPLE_CHUNKS = 8;

function bytesToHex(bytes: Uint8Array): string {
    return Array.from(bytes)
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
}

function bytesIncludeSequence(bytes: Uint8Array, sequence: number[]): boolean {
    if (sequence.length === 0 || bytes.length < sequence.length) {
        return false;
    }

    for (let index = 0; index <= bytes.length - sequence.length; index += 1) {
        const matches = sequence.every(
            (byte, offset) => bytes[index + offset] === byte,
        );
        if (matches) {
            return true;
        }
    }

    return false;
}

export function isMp4MimeType(mimeType: string | null | undefined): boolean {
    const normalizedMimeType = mimeType?.toLowerCase().split(";")[0].trim();
    return (
        normalizedMimeType === "audio/mp4" ||
        normalizedMimeType === "video/mp4" ||
        normalizedMimeType === "audio/x-m4a"
    );
}

export function isWebmMimeType(mimeType: string | null | undefined): boolean {
    const normalizedMimeType = mimeType?.toLowerCase().split(";")[0].trim();
    return (
        normalizedMimeType === "audio/webm" ||
        normalizedMimeType === "video/webm"
    );
}

export async function blobStartsWithEbmlHeader(blob: Blob): Promise<boolean> {
    if (blob.size < EBML_HEADER_BYTES.length) {
        return false;
    }

    const bytes = new Uint8Array(
        await blob.slice(0, EBML_HEADER_BYTES.length).arrayBuffer(),
    );

    return EBML_HEADER_BYTES.every((byte, index) => bytes[index] === byte);
}

export async function inspectPersistedChunk(
    chunk: Pick<PersistedChunkRecord, "index" | "size" | "blob">,
): Promise<PersistedChunkDiagnostics> {
    const scanBytes = new Uint8Array(
        await chunk.blob.slice(0, CHUNK_DIAGNOSTIC_SCAN_BYTES).arrayBuffer(),
    );
    const firstBytes = scanBytes.slice(0, CHUNK_DIAGNOSTIC_FIRST_BYTES);

    return {
        version: 1,
        index: chunk.index,
        size: chunk.size,
        type: chunk.blob.type,
        firstBytesHex: bytesToHex(firstBytes),
        startsWithEbmlHeader: EBML_HEADER_BYTES.every(
            (byte, index) => scanBytes[index] === byte,
        ),
        hasOpusHeadNearStart: bytesIncludeSequence(scanBytes, OPUS_HEAD_BYTES),
    };
}

export async function inspectMaterializedRecordingIntegrity(
    session: PersistedSessionMeta,
    chunks: PersistedChunkRecord[],
    blob: Blob,
): Promise<MaterializedRecordingIntegrity> {
    const isWebm = isWebmMimeType(session.mimeType);
    const isMp4 = isMp4MimeType(session.mimeType);
    if (!isWebm && !isMp4) {
        // AIDEV-NOTE: Reason stays "not-webm" for back-compat with stored
        // diagnostics sidecars; it now means "container we cannot inspect".
        return { valid: true, reason: "not-webm" };
    }

    if (blob.size === 0 || chunks.length === 0) {
        return {
            valid: false,
            reason: "empty-webm",
            detail: "Persisted recording has no stored chunks.",
        };
    }

    const firstChunk = chunks[0];
    if (firstChunk.index !== 0) {
        return {
            valid: false,
            reason: "missing-first-chunk",
            detail: `First persisted chunk index is ${firstChunk.index}, expected 0.`,
        };
    }

    // AIDEV-NOTE: Chunks are concatenated in index order with no gap check, so a
    // hole in the middle silently yields a truncated file. Surface it here.
    const expectedLastIndex = chunks.length - 1;
    const actualLastIndex = chunks[chunks.length - 1].index;
    if (actualLastIndex !== expectedLastIndex) {
        return {
            valid: false,
            reason: "chunk-index-gap",
            detail: `Persisted chunk indices are not contiguous: ${chunks.length} chunks ending at index ${actualLastIndex}.`,
        };
    }

    try {
        const headerBytes = new Uint8Array(
            await firstChunk.blob
                .slice(0, CONTAINER_SNIFF_BYTES)
                .arrayBuffer(),
        );

        if (isWebm && !startsWithEbmlHeader(headerBytes)) {
            return {
                valid: false,
                reason: "missing-ebml-header",
                detail: "First persisted WebM chunk is missing the EBML header.",
            };
        }

        if (isMp4 && !startsWithFtypBox(headerBytes)) {
            return {
                valid: false,
                reason: "missing-mp4-init-segment",
                detail: "First persisted MP4 chunk is missing the ftyp init segment.",
            };
        }
    } catch (error) {
        return {
            valid: false,
            reason: "header-read-failed",
            detail:
                error instanceof Error
                    ? error.message
                    : "Unable to read container header bytes.",
        };
    }

    return { valid: true, reason: "valid" };
}

export function buildRecordingWebmDiagnostics(
    session: PersistedSessionMeta,
    chunks: PersistedChunkRecord[],
    blob: Blob,
    integrity: MaterializedRecordingIntegrity,
): PersistedRecordingWebmDiagnostics {
    const chunkDiagnostics = chunks
        .map((chunk) => chunk.diagnostics)
        .filter((diagnostic): diagnostic is PersistedChunkDiagnostics =>
            Boolean(diagnostic),
        );
    const firstChunk = chunkDiagnostics.find(
        (diagnostic) => diagnostic.index === 0,
    );

    return {
        version: 1,
        mimeType: session.mimeType,
        chunkMs: session.chunkMs,
        chunkCount: chunks.length,
        totalBytes: chunks.reduce((total, chunk) => total + chunk.size, 0),
        materializedBytes: blob.size,
        integrity,
        firstObservedChunkIndex: chunks[0]?.index ?? null,
        lastObservedChunkIndex: chunks[chunks.length - 1]?.index ?? null,
        firstChunk: firstChunk ?? null,
        sampledChunks: chunkDiagnostics.slice(
            0,
            RECORDING_DIAGNOSTIC_SAMPLE_CHUNKS,
        ),
    };
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () =>
            reject(request.error ?? new Error("IndexedDB request failed"));
    });
}

function transactionToPromise(transaction: IDBTransaction): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onabort = () =>
            reject(
                transaction.error ?? new Error("IndexedDB transaction aborted"),
            );
        transaction.onerror = () =>
            reject(
                transaction.error ?? new Error("IndexedDB transaction failed"),
            );
    });
}

async function openRecorderDb(dbName = DB_NAME): Promise<IDBDatabase> {
    if (dbPromiseCache.has(dbName)) {
        return dbPromiseCache.get(dbName)!;
    }

    const openPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const openRequest = indexedDB.open(dbName, DB_VERSION);

        openRequest.onupgradeneeded = () => {
            const database = openRequest.result;

            if (!database.objectStoreNames.contains(SESSIONS_STORE)) {
                database.createObjectStore(SESSIONS_STORE, { keyPath: "id" });
            }

            if (!database.objectStoreNames.contains(CHUNKS_STORE)) {
                const chunksStore = database.createObjectStore(CHUNKS_STORE, {
                    keyPath: ["sessionId", "index"],
                });
                chunksStore.createIndex(CHUNKS_BY_SESSION_INDEX, "sessionId", {
                    unique: false,
                });
            } else {
                const transaction = openRequest.transaction;
                if (transaction) {
                    const chunksStore = transaction.objectStore(CHUNKS_STORE);
                    if (
                        !chunksStore.indexNames.contains(
                            CHUNKS_BY_SESSION_INDEX,
                        )
                    ) {
                        chunksStore.createIndex(
                            CHUNKS_BY_SESSION_INDEX,
                            "sessionId",
                            { unique: false },
                        );
                    }
                }
            }
        };

        openRequest.onsuccess = () => {
            const database = openRequest.result;
            // AIDEV-NOTE: Evict the cached connection whenever it closes. The
            // cache used to keep handing out a closed IDBDatabase after a
            // versionchange/unexpected close, so every later transaction threw
            // "The database connection is closing" until a page reload.
            const evict = () => {
                if (dbPromiseCache.get(dbName) === openPromise) {
                    dbPromiseCache.delete(dbName);
                }
            };
            database.onversionchange = () => {
                evict();
                database.close();
            };
            database.onclose = evict;
            resolve(database);
        };

        openRequest.onerror = () => {
            logger.error("Failed to open recorder database");
            reject(
                openRequest.error ??
                    new Error("Failed to open recorder database"),
            );
        };
        openRequest.onblocked = () => {
            logger.warn("Recorder database upgrade blocked by another tab");
            reject(
                new Error("Recorder database upgrade blocked by another tab"),
            );
        };
    });

    dbPromiseCache.set(dbName, openPromise);
    return openPromise;
}

export async function putSessionMeta(
    session: PersistedSessionMeta,
    dbName = DB_NAME,
): Promise<void> {
    const db = await openRecorderDb(dbName);
    const tx = db.transaction(SESSIONS_STORE, "readwrite");
    tx.objectStore(SESSIONS_STORE).put(session);
    await transactionToPromise(tx);
}

export async function getSessionMeta(
    sessionId: string,
    dbName = DB_NAME,
): Promise<PersistedSessionMeta | null> {
    const db = await openRecorderDb(dbName);
    const tx = db.transaction(SESSIONS_STORE, "readonly");
    const request = tx.objectStore(SESSIONS_STORE).get(sessionId);
    const result = await requestToPromise<PersistedSessionMeta | undefined>(
        request,
    );
    await transactionToPromise(tx);
    return result ?? null;
}

export async function setSessionUploadSessionId(
    sessionId: string,
    uploadSessionId: string,
    dbName = DB_NAME,
): Promise<void> {
    const db = await openRecorderDb(dbName);
    const tx = db.transaction(SESSIONS_STORE, "readwrite");
    const store = tx.objectStore(SESSIONS_STORE);
    const request = store.get(sessionId);
    const session = await requestToPromise<PersistedSessionMeta | undefined>(
        request,
    );

    if (session) {
        // AIDEV-NOTE: Preserve updatedAt: it is the recorder heartbeat, not a generic row timestamp.
        store.put({ ...session, uploadSessionId });
    }

    await transactionToPromise(tx);
}

export async function listSessionMetas(
    dbName = DB_NAME,
): Promise<PersistedSessionMeta[]> {
    const db = await openRecorderDb(dbName);
    const tx = db.transaction(SESSIONS_STORE, "readonly");
    const request = tx.objectStore(SESSIONS_STORE).getAll();
    const results = await requestToPromise<PersistedSessionMeta[]>(request);
    await transactionToPromise(tx);
    return results.sort(
        (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
}

export async function putChunk(
    chunk: PersistedChunkRecord,
    dbName = DB_NAME,
): Promise<void> {
    const db = await openRecorderDb(dbName);
    const tx = db.transaction(CHUNKS_STORE, "readwrite");
    tx.objectStore(CHUNKS_STORE).put(chunk);
    await transactionToPromise(tx);
}

// AIDEV-NOTE: Primary-key range over the compound keyPath ["sessionId","index"].
// Chunks are NOT read through the bySessionId secondary index any more: WebKit
// returned only a single record from that index for long recordings, so a
// 28-minute session materialized as one ~1s chunk and uploaded as a 28KB file
// (4 confirmed data-loss cases, all Safari/iOS, Aug-Sep 2026). The primary key
// is written by putChunk itself and needs no separate index maintenance.
export function chunkKeyRangeForSession(sessionId: string): IDBKeyRange {
    return IDBKeyRange.bound([sessionId, -Infinity], [sessionId, Infinity]);
}

export async function loadChunks(
    sessionId: string,
    dbName = DB_NAME,
): Promise<PersistedChunkRecord[]> {
    const db = await openRecorderDb(dbName);
    const tx = db.transaction(CHUNKS_STORE, "readonly");
    const store = tx.objectStore(CHUNKS_STORE);
    const request = store.getAll(chunkKeyRangeForSession(sessionId));
    const chunks = await requestToPromise<PersistedChunkRecord[]>(request);
    await transactionToPromise(tx);
    return chunks.sort((a, b) => a.index - b.index);
}

export async function materializeSession(
    sessionId: string,
    dbName = DB_NAME,
): Promise<MaterializedRecording | null> {
    const [session, chunks] = await Promise.all([
        getSessionMeta(sessionId, dbName),
        loadChunks(sessionId, dbName),
    ]);

    if (!session) {
        return null;
    }

    const sortedChunks = chunks.sort((a, b) => a.index - b.index);
    const blob = new Blob(
        sortedChunks.map((chunk) => chunk.blob),
        {
            type: session.mimeType || undefined,
        },
    );
    const integrity = await inspectMaterializedRecordingIntegrity(
        session,
        sortedChunks,
        blob,
    );
    const webmDiagnostics = buildRecordingWebmDiagnostics(
        session,
        sortedChunks,
        blob,
        integrity,
    );

    return {
        session: {
            ...session,
            webmDiagnostics,
        },
        chunks: sortedChunks,
        blob,
        integrity,
        webmDiagnostics,
    };
}

export async function deleteSession(
    sessionId: string,
    dbName = DB_NAME,
): Promise<void> {
    const db = await openRecorderDb(dbName);
    const tx = db.transaction([SESSIONS_STORE, CHUNKS_STORE], "readwrite");

    tx.objectStore(SESSIONS_STORE).delete(sessionId);

    // AIDEV-NOTE: Delete over the PRIMARY key range, never the bySessionId
    // index — the index missed records on Safari, which is how orphaned chunks
    // accumulated after a "successful" upload. A single range delete replaces
    // the old cursor walk (one request per chunk; hours-long recordings at
    // 1 chunk/s meant thousands of round-trips inside one readwrite
    // transaction, which is where WebKit stalled).
    tx.objectStore(CHUNKS_STORE).delete(chunkKeyRangeForSession(sessionId));

    await transactionToPromise(tx);
}

export async function requestPersistentStorage(): Promise<boolean | null> {
    if (typeof navigator === "undefined" || !navigator.storage?.persist) {
        return null;
    }

    try {
        return await navigator.storage.persist();
    } catch {
        return null;
    }
}

// AIDEV-NOTE: Orphaned-chunk rescue (Sep 2026). Before the primary-key fix
// above, loadChunks AND deleteSession both walked the bySessionId index. On
// WebKit that index returned a single record, so upload shipped one chunk and
// cleanup deleted only that one — leaving the rest of the recording stranded in
// IndexedDB with its sessions-store row gone. This scan walks the chunks store
// directly (no index, no meta) and rebuilds the missing meta row so the normal
// recovery surfaces can list, preview, and re-upload the audio.

export interface OrphanedChunkGroup {
    sessionId: string;
    chunkCount: number;
    totalBytes: number;
    firstIndex: number;
    lastIndex: number;
    firstCreatedAt: string | null;
    mimeType: string;
}

export interface OrphanRepairResult {
    scannedChunks: number;
    groups: OrphanedChunkGroup[];
    repairedSessionIds: string[];
    reconciledSessionIds: string[];
}

/**
 * Group every record in the chunks store by sessionId, reading the store
 * directly rather than through the bySessionId index.
 */
export async function scanChunkStoreBySession(
    dbName = DB_NAME,
): Promise<{ scanned: number; groups: Map<string, OrphanedChunkGroup> }> {
    const db = await openRecorderDb(dbName);
    const tx = db.transaction(CHUNKS_STORE, "readonly");
    const cursorRequest = tx.objectStore(CHUNKS_STORE).openCursor();
    const groups = new Map<string, OrphanedChunkGroup>();
    let scanned = 0;

    await new Promise<void>((resolve, reject) => {
        cursorRequest.onerror = () =>
            reject(
                cursorRequest.error ?? new Error("Failed to scan chunk store"),
            );
        cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result;
            if (!cursor) {
                resolve();
                return;
            }

            const record = cursor.value as PersistedChunkRecord;
            scanned += 1;

            const existing = groups.get(record.sessionId);
            if (existing) {
                existing.chunkCount += 1;
                existing.totalBytes += record.size;
                existing.firstIndex = Math.min(
                    existing.firstIndex,
                    record.index,
                );
                existing.lastIndex = Math.max(existing.lastIndex, record.index);
                if (!existing.mimeType && record.blob?.type) {
                    existing.mimeType = record.blob.type;
                }
                if (
                    record.createdAt &&
                    (!existing.firstCreatedAt ||
                        record.createdAt < existing.firstCreatedAt)
                ) {
                    existing.firstCreatedAt = record.createdAt;
                }
            } else {
                groups.set(record.sessionId, {
                    sessionId: record.sessionId,
                    chunkCount: 1,
                    totalBytes: record.size,
                    firstIndex: record.index,
                    lastIndex: record.index,
                    firstCreatedAt: record.createdAt ?? null,
                    mimeType:
                        record.blob?.type ?? record.diagnostics?.type ?? "",
                });
            }

            cursor.continue();
        };
    });

    await transactionToPromise(tx);
    return { scanned, groups };
}

/**
 * Rebuild sessions-store metadata for chunk groups whose meta row is missing,
 * and correct meta rows that understate what is actually persisted. Returns a
 * summary; never throws for an individual group.
 */
export interface RepairOrphanedChunkOptions {
    /**
     * AIDEV-NOTE: Stamped onto rebuilt rows. The deleted sessions-store row held
     * the original recordedByUserId and chunks do not carry it, so ownership is
     * unrecoverable from storage alone. IndexedDB is per-origin, and unstamped
     * rows are visible to every account on the browser — so we attribute the
     * rescue to the (flag-gated, self-initiated) user running it rather than
     * leaving stranded audio readable by anyone who signs in on this device.
     */
    recordedByUserId?: string | null;
}

export async function repairOrphanedChunkSessions(
    dbName = DB_NAME,
    options: RepairOrphanedChunkOptions = {},
): Promise<OrphanRepairResult> {
    const { scanned, groups } = await scanChunkStoreBySession(dbName);
    const repairedSessionIds: string[] = [];
    const reconciledSessionIds: string[] = [];

    for (const group of groups.values()) {
        const existing = await getSessionMeta(group.sessionId, dbName).catch(
            () => null,
        );

        if (!existing) {
            // AIDEV-NOTE: elapsedMs is reconstructed from chunk span x chunkMs.
            // It is an estimate used only for display; the audio itself is exact.
            const createdAt = group.firstCreatedAt ?? nowIso();
            const rebuilt: PersistedSessionMeta = {
                id: group.sessionId,
                status: "failed",
                mimeType: group.mimeType,
                chunkMs: DEFAULT_ORPHAN_CHUNK_MS,
                chunkCount: group.chunkCount,
                totalBytes: group.totalBytes,
                elapsedMs:
                    (group.lastIndex - group.firstIndex + 1) *
                    DEFAULT_ORPHAN_CHUNK_MS,
                partial: true,
                createdAt,
                updatedAt: nowIso(),
                startedAt: createdAt,
                stoppedAt: nowIso(),
                recordedByUserId: options.recordedByUserId ?? null,
                // AIDEV-NOTE: practitionerId/clientId/uploadSessionId died with
                // the sessions row and cannot be recovered from chunk records.
                // The retry flow already re-prompts for a client when clientId
                // is absent (see use-recording-retry clientIdOverride), and the
                // original upload slot is not resumable anyway once it has left
                // CREATED+EMPTY, so a rescued recording lands in a new session.
                practitionerId: null,
                clientId: null,
                recoveredFromOrphanedChunks: true,
            };

            try {
                await putSessionMeta(rebuilt, dbName);
                repairedSessionIds.push(group.sessionId);
            } catch {
                // Best effort — a group we cannot rebuild is simply not offered.
            }
            continue;
        }

        if (
            existing.chunkCount < group.chunkCount ||
            existing.totalBytes < group.totalBytes
        ) {
            try {
                await putSessionMeta(
                    {
                        ...existing,
                        status:
                            existing.status === "recording" ||
                            existing.status === "paused"
                                ? existing.status
                                : "failed",
                        chunkCount: group.chunkCount,
                        totalBytes: group.totalBytes,
                        partial: true,
                        updatedAt: nowIso(),
                        recoveredFromOrphanedChunks: true,
                    },
                    dbName,
                );
                reconciledSessionIds.push(group.sessionId);
            } catch {
                // Best effort.
            }
        }
    }

    return {
        scannedChunks: scanned,
        groups: [...groups.values()],
        repairedSessionIds,
        reconciledSessionIds,
    };
}
